/**
 * Purpose: Transport-free building blocks for the copilot's in-chat
 *          realtime voice mode (`<ada-copilot voice="realtime">`): the
 *          session instructions (chat prompt + spoken-style rules + the
 *          chat history so far) and {@link TalkTurnSequencer}, which turns
 *          the realtime event stream into chat messages in the right order.
 *
 * Implementation notes:
 *   - The chat history goes into the session *instructions*, not into
 *     seeded `conversation.item.create` items. Instructions are accepted
 *     by every realtime model and output modality, need no datachannel
 *     timing, and cost the same tokens; seeded assistant items depend on
 *     the session's output modality (`output_text` vs `output_audio`).
 *   - Ordering: OpenAI may deliver the user's transcript after the
 *     assistant has started replying. The sequencer reserves a slot for a
 *     spoken user turn when it is committed (`input-committed`) and only
 *     releases turns from the head of the queue, so the saved order always
 *     matches the conversation. A user slot whose transcript never comes
 *     is dropped after {@link TalkTurnSequencerOptions.userTranscriptTimeoutMs}.
 *   - An assistant turn is held until its text is final AND its audio has
 *     stopped (or was cut). Over WebRTC the transcript finishes long before
 *     playback does, and a barge-in arrives during playback — holding the
 *     turn is what lets it be marked as interrupted. Turns whose response
 *     never reported audio are released as soon as their text is final.
 *   - No timers inside: the host calls {@link TalkTurnSequencer.expire}
 *     with the current time. Keeps the class deterministic under test.
 *
 * Created: 2026-10-06 16:30
 *
 * Changelog:
 *   - 2026-10-06 16:30 — Initial version. Backs the opt-in in-chat realtime
 *     voice mode of @ada/copilot-widget.
 */
// ── Instructions ────────────────────────────────────────────────
/** Spoken-style rules appended to the chat prompt for a voice session. */
export const REALTIME_TALK_STYLE = [
    'You are now in a live voice conversation: the user speaks to you and hears your replies.',
    'Speak naturally and briefly: one to three short sentences per turn.',
    'Never use markdown, lists, tables, emojis, URLs or code — none of it can be heard.',
    'When an answer needs detail, give the key point first and offer to go deeper',
    'or to show it on screen with the app tools.',
    'If the user interrupts you, stop and respond to what they just said.',
    'Before a tool call that may take a moment, say a very short phrase first',
    '(for example "un momento" or "one moment", in the user’s language).',
].join(' ');
/**
 * Build the instructions for an in-chat voice session: the chat prompt,
 * the spoken-style rules and, when the chat already has messages, the
 * recent history so the voice turn continues the same conversation.
 */
export function buildRealtimeTalkInstructions(input) {
    const parts = [input.basePrompt, REALTIME_TALK_STYLE];
    const history = renderHistory(input.history ?? [], input.limits ?? {});
    if (history) {
        parts.push('Earlier in this conversation (typed chat shown on screen, oldest first). ' +
            'Continue from here and do not repeat it:\n' +
            history);
    }
    return parts.join('\n\n');
}
function renderHistory(history, limits) {
    const maxMessages = limits.maxMessages ?? 20;
    const maxPer = limits.maxCharsPerMessage ?? 1200;
    const maxTotal = limits.maxTotalChars ?? 12_000;
    const lines = [];
    for (const msg of history.slice(-maxMessages)) {
        let text = msg.content.trim();
        const files = (msg.attachments ?? []).map((a) => `[file: ${a.name}]`).join(' ');
        if (files)
            text = text ? `${text} ${files}` : files;
        if (!text)
            continue;
        if (text.length > maxPer)
            text = `${text.slice(0, maxPer).trimEnd()}…`;
        lines.push(`${msg.role === 'user' ? 'User' : 'Assistant'}: ${text}`);
    }
    // Drop from the oldest end until the block fits the budget.
    let total = lines.reduce((n, l) => n + l.length + 1, 0);
    while (lines.length > 0 && total > maxTotal) {
        total -= lines.shift().length + 1;
    }
    return lines.join('\n');
}
/**
 * Orders realtime voice events into chat turns. Feed it events as they
 * arrive, then call {@link drain} to collect the turns that are ready, in
 * conversation order. See the file header for the rules.
 */
export class TalkTurnSequencer {
    #timeoutMs;
    #playbackTimeoutMs;
    #slots = [];
    #seq = 0;
    /**
     * Playback that started before the reply's first transcript delta. The
     * next assistant slot for that response inherits it. `null` = no id.
     */
    #pendingAudio;
    constructor(opts = {}) {
        this.#timeoutMs = opts.userTranscriptTimeoutMs ?? 8000;
        this.#playbackTimeoutMs = opts.playbackTimeoutMs ?? 120_000;
    }
    /** A spoken user turn was committed; reserve its place in the order. */
    userCommitted(itemId, now = Date.now()) {
        if (this.#find('user', itemId))
            return;
        this.#slots.push(this.#slot('user', itemId, now));
    }
    /**
     * The transcript of a spoken user turn arrived. An empty text releases
     * the slot without producing a turn (noise, failed transcription).
     */
    userTranscript(itemId, text, now = Date.now()) {
        const slot = itemId ? this.#find('user', itemId) : undefined;
        if (slot) {
            slot.text = text.trim();
            this.#markTextDone(slot, now);
            return;
        }
        if (!text.trim())
            return;
        const fresh = this.#slot('user', itemId ?? this.#nextKey('u'), now);
        fresh.text = text.trim();
        this.#markTextDone(fresh, now);
        this.#slots.push(fresh);
    }
    /** A typed user message sent into the voice session (already final). */
    userTyped(text, now = Date.now()) {
        const trimmed = text.trim();
        if (!trimmed)
            return;
        const slot = this.#slot('user', this.#nextKey('typed'), now);
        slot.text = trimmed;
        this.#markTextDone(slot, now);
        this.#slots.push(slot);
    }
    /** A piece of the assistant's live transcript. */
    assistantDelta(itemId, delta, responseId, now = Date.now()) {
        const slot = this.#assistantSlot(itemId, responseId, now);
        slot.text += delta;
    }
    /** The assistant's final transcript for an item. */
    assistantDone(itemId, text, responseId, now = Date.now()) {
        const slot = this.#assistantSlot(itemId, responseId, now);
        if (text.trim())
            slot.text = text;
        this.#markTextDone(slot, now);
    }
    /**
     * Assistant playback changed. `cleared` marks the reply as interrupted.
     * Without a `responseId` the newest held assistant turn is used.
     */
    assistantAudio(state, responseId) {
        const slots = this.#slots.filter((s) => s.role === 'assistant' && (responseId === undefined || s.responseId === responseId));
        if (state === 'started') {
            const target = slots[slots.length - 1];
            if (target)
                target.audioPlaying = true;
            else
                this.#pendingAudio = responseId ?? null;
            return;
        }
        for (const s of slots) {
            s.audioPlaying = false;
            if (state === 'cleared')
                s.interrupted = true;
        }
        if (this.#pendingAudio !== undefined &&
            (responseId === undefined || this.#pendingAudio === responseId)) {
            this.#pendingAudio = undefined;
        }
    }
    /**
     * A response ended: no more text will come for it, so its turns count as
     * final even if a cancelled response skipped its `done` transcript.
     * `cancelled` marks them as interrupted.
     */
    responseDone(status, responseId, now = Date.now()) {
        for (const s of this.#slots) {
            if (s.role !== 'assistant')
                continue;
            if (responseId !== undefined && s.responseId !== undefined && s.responseId !== responseId) {
                continue;
            }
            if (!s.textDone)
                this.#markTextDone(s, now);
            if (status === 'cancelled')
                s.interrupted = true;
        }
    }
    /**
     * Drop user slots whose transcript is overdue, and stop waiting on
     * playback that never reported its end.
     */
    expire(now = Date.now()) {
        this.#slots = this.#slots.filter((s) => !(s.role === 'user' && !s.textDone && now - s.openedAt > this.#timeoutMs));
        for (const s of this.#slots) {
            if (s.audioPlaying && s.textDone && now - s.textDoneAt > this.#playbackTimeoutMs) {
                s.audioPlaying = false;
            }
        }
    }
    /** Remove and return every turn that is ready, in conversation order. */
    drain() {
        const out = [];
        while (this.#slots.length > 0) {
            const head = this.#slots[0];
            if (!head.textDone || head.audioPlaying)
                break;
            this.#slots.shift();
            const content = head.text.trim();
            if (!content)
                continue;
            out.push({
                role: head.role,
                content,
                ...(head.role === 'assistant' && head.interrupted ? { interrupted: true } : {}),
            });
        }
        return out;
    }
    /** Live text of the newest assistant turn still being held, if any. */
    get liveAssistantText() {
        for (let i = this.#slots.length - 1; i >= 0; i -= 1) {
            const s = this.#slots[i];
            if (s.role === 'assistant')
                return s.text;
        }
        return '';
    }
    /** True while any turn is held (waiting for text or playback). */
    get hasPending() {
        return this.#slots.length > 0;
    }
    /**
     * The session ended: release every held turn, in order. A reply that was
     * still being generated or played is marked as interrupted; user turns
     * still waiting for a transcript are dropped.
     */
    flush() {
        for (const s of this.#slots) {
            if (s.role !== 'assistant')
                continue;
            if (s.audioPlaying || !s.textDone)
                s.interrupted = true;
            s.audioPlaying = false;
            s.textDone = true;
        }
        this.#slots = this.#slots.filter((s) => s.textDone);
        const out = this.drain();
        this.#slots = [];
        this.#pendingAudio = undefined;
        return out;
    }
    // ── internals ──
    #assistantSlot(itemId, responseId, now) {
        let slot = itemId ? this.#find('assistant', itemId) : undefined;
        if (!slot && !itemId) {
            // No ids (beta events): continue the newest assistant turn that is
            // still receiving text.
            for (let i = this.#slots.length - 1; i >= 0; i -= 1) {
                const s = this.#slots[i];
                if (s.role === 'assistant' && !s.textDone) {
                    slot = s;
                    break;
                }
            }
        }
        if (!slot) {
            slot = this.#slot('assistant', itemId ?? this.#nextKey('a'), now);
            if (this.#pendingAudio !== undefined &&
                (this.#pendingAudio === null || this.#pendingAudio === responseId)) {
                slot.audioPlaying = true;
                this.#pendingAudio = undefined;
            }
            this.#slots.push(slot);
        }
        if (responseId && !slot.responseId)
            slot.responseId = responseId;
        return slot;
    }
    #markTextDone(slot, now) {
        slot.textDone = true;
        slot.textDoneAt = now;
    }
    #find(role, key) {
        return this.#slots.find((s) => s.role === role && s.key === key);
    }
    #slot(role, key, now) {
        return {
            key,
            role,
            text: '',
            textDone: false,
            textDoneAt: 0,
            audioPlaying: false,
            interrupted: false,
            openedAt: now,
        };
    }
    #nextKey(prefix) {
        this.#seq += 1;
        return `${prefix}-${this.#seq}`;
    }
}
//# sourceMappingURL=realtime-talk.js.map